import type { AppVersion, UpdateAction } from '@konyaxl/shared';
import { decideUpdate } from '@konyaxl/shared';
import { getNativeVersion, installApk, isNative } from './native.js';

/**
 * Güncelleme denetimi.
 *
 * İki katman:
 *  - Arayüz: sunucudaki `webBuild` büyükse sayfa yeniden yüklenir. Capacitor
 *    varlıkları sunucudan aldığı için bu APK'ya dokunmadan çalışır.
 *  - APK: `nativeCode` büyükse zorunlu güncelleme ekranı gösterilir.
 */

const KONTROL_ARALIGI_MS = 15 * 60 * 1000;
/** Aynı yayın için tekrar tekrar yeniden yüklemeyi engeller. */
const RELOAD_ANAHTARI = 'konyaxl.lastReload';

export interface UpdateState {
  action: UpdateAction;
  server: AppVersion | null;
}

export async function fetchVersion(): Promise<AppVersion | null> {
  try {
    const res = await fetch('/api/app/version', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as AppVersion;
  } catch {
    // Sunucuya ulaşılamıyorsa güncelleme kontrolü atlanır; uygulama çalışmaya
    // devam eder. Depo bağlantısı koptuğunda çalışmayı durdurmak yanlış olur.
    return null;
  }
}

export async function checkForUpdate(): Promise<UpdateState> {
  /**
   * Geliştirmede kontrol yapılmaz: dev sunucusunda `__WEB_BUILD__` her zaman 0
   * olduğu için sunucudaki her yayın numarası büyük görünür ve sayfa
   * durmadan yeniden yüklenirdi. Vite'ın kendi sıcak güncellemesi zaten var.
   */
  if (import.meta.env.DEV) return { action: 'none', server: null };

  const server = await fetchVersion();
  if (!server) return { action: 'none', server: null };

  const action = decideUpdate(server, {
    nativeCode: await getNativeVersion(),
    webBuild: __WEB_BUILD__,
  });

  return { action, server };
}

/**
 * Arayüzü yeniler.
 *
 * Aynı yayın için ikinci kez yeniden yüklemez: sunucu yanlış bir `webBuild`
 * bildirirse uygulama sonsuz yeniden yükleme döngüsüne girerdi.
 */
export function reloadWebAssets(webBuild: number): void {
  const son = Number(sessionStorage.getItem(RELOAD_ANAHTARI) ?? '0');
  if (son === webBuild) return;

  sessionStorage.setItem(RELOAD_ANAHTARI, String(webBuild));
  window.location.reload();
}

export async function startUpdateApk(
  server: AppVersion,
  onProgress?: (yuzde: number) => void,
): Promise<void> {
  if (!server.apkUrl) throw new Error('Güncelleme dosyası tanımlı değil');
  if (!isNative()) throw new Error('APK kurulumu yalnızca uygulama içinde yapılabilir');

  await installApk(server.apkUrl, onProgress);
}

/**
 * Periyodik denetim. Vardiya ortasında yayınlanan bir düzeltme, cihaz
 * yeniden başlatılmadan da ulaşsın diye.
 */
export function startPeriodicCheck(onUpdate: (state: UpdateState) => void): () => void {
  let durduruldu = false;

  const calistir = async () => {
    if (durduruldu) return;
    const state = await checkForUpdate();
    if (!durduruldu && state.action !== 'none') onUpdate(state);
  };

  const zamanlayici = setInterval(() => void calistir(), KONTROL_ARALIGI_MS);

  /** Uygulama arka plandan dönünce hemen kontrol edilir. */
  const gorunurluk = () => {
    if (document.visibilityState === 'visible') void calistir();
  };
  document.addEventListener('visibilitychange', gorunurluk);

  return () => {
    durduruldu = true;
    clearInterval(zamanlayici);
    document.removeEventListener('visibilitychange', gorunurluk);
  };
}
